/**
 * Pure aggregator for the room-discovery report. Lives outside the hook
 * file so tests can import it without the Supabase client / env-var
 * dependency that useSupabase pulls in.
 */

export interface RoomDiscoveryByBuildingRow {
  buildingId:        string | null
  buildingName:      string
  totalEvents:       number
  uniqueRoomCount:   number
}

export interface RoomDiscoveryReport {
  rangeDays:        number
  totalEvents:      number
  uniqueRoomCount:  number
  byBuilding:       RoomDiscoveryByBuildingRow[]
}

export interface AuditClaimRow {
  metadata_json: {
    building_id?:        string
    room_number?:        string
    was_created?:        boolean
    floor_was_created?:  boolean
  } | null
  entity_id:    string | null
  created_at:   string
  /** Set only on rows fetched via the multi-org aggregator. */
  organization_id?: string
}

export interface BuildingRow {
  id:               string
  name:             string
  /** Set only on rows fetched via the multi-org aggregator. */
  organization_id?: string
}

export interface OrgRow {
  id:   string
  name: string
}

export interface RoomDiscoveryByOrgAndBuildingRow {
  organizationId:    string
  organizationName:  string
  buildingId:        string | null
  buildingName:      string
  totalEvents:       number
  uniqueRoomCount:   number
}

export interface RoomDiscoveryMultiOrgReport {
  rangeDays:        number
  totalEvents:      number
  uniqueRoomCount:  number
  byOrgAndBuilding: RoomDiscoveryByOrgAndBuildingRow[]
}

export function aggregateRoomDiscovery(
  rangeDays:    number,
  claimRows:    readonly AuditClaimRow[],
  buildingRows: readonly BuildingRow[],
): RoomDiscoveryReport {
  const buildingNameById = new Map<string, string>()
  for (const b of buildingRows) buildingNameById.set(b.id, b.name)

  const roomsByBuilding   = new Map<string | null, Set<string>>()
  const eventsByBuilding  = new Map<string | null, number>()
  const allRooms          = new Set<string>()

  for (const row of claimRows) {
    const buildingId = row.metadata_json?.building_id ?? null
    const roomKey    = row.entity_id ?? `${buildingId ?? 'unknown'}/${row.metadata_json?.room_number ?? ''}`

    eventsByBuilding.set(buildingId, (eventsByBuilding.get(buildingId) ?? 0) + 1)

    let set = roomsByBuilding.get(buildingId)
    if (!set) { set = new Set<string>(); roomsByBuilding.set(buildingId, set) }
    set.add(roomKey)

    allRooms.add(roomKey)
  }

  const byBuilding: RoomDiscoveryByBuildingRow[] = []
  for (const [buildingId, totalEvents] of eventsByBuilding.entries()) {
    byBuilding.push({
      buildingId,
      buildingName:    buildingId ? (buildingNameById.get(buildingId) ?? 'Unknown building') : 'Unknown building',
      totalEvents,
      uniqueRoomCount: roomsByBuilding.get(buildingId)?.size ?? 0,
    })
  }
  byBuilding.sort((a, b) =>
    b.uniqueRoomCount - a.uniqueRoomCount
    || b.totalEvents - a.totalEvents
    || a.buildingName.localeCompare(b.buildingName),
  )

  return {
    rangeDays,
    totalEvents:     claimRows.length,
    uniqueRoomCount: allRooms.size,
    byBuilding,
  }
}

/**
 * Multi-org aggregator. Walks claim rows tagged with organization_id,
 * groups by (org, building), and returns a flat per-(org, building)
 * list. Powers the Reports "All my orgs" toggle (M4b).
 *
 * Each row in the input must carry organization_id — the multi-org
 * Edge Function returns audit_logs rows with that field set.
 * Buildings + orgs lookup tables resolve display names.
 */
export function aggregateRoomDiscoveryMultiOrg(
  rangeDays:    number,
  claimRows:    readonly AuditClaimRow[],
  buildingRows: readonly BuildingRow[],
  orgRows:      readonly OrgRow[],
): RoomDiscoveryMultiOrgReport {
  const buildingNameById = new Map<string, string>()
  for (const b of buildingRows) buildingNameById.set(b.id, b.name)
  const orgNameById = new Map<string, string>()
  for (const o of orgRows) orgNameById.set(o.id, o.name)

  type Key = string  // `${orgId}::${buildingId ?? 'unknown'}`
  const events    = new Map<Key, number>()
  const uniqueRooms = new Map<Key, Set<string>>()
  const allRooms  = new Set<string>()

  for (const row of claimRows) {
    const orgId      = row.organization_id ?? ''
    if (!orgId) continue  // un-tagged row — skip rather than misattribute
    const buildingId = row.metadata_json?.building_id ?? null
    const roomKey    = row.entity_id ?? `${buildingId ?? 'unknown'}/${row.metadata_json?.room_number ?? ''}`
    const key        = `${orgId}::${buildingId ?? 'unknown'}`

    events.set(key, (events.get(key) ?? 0) + 1)
    let set = uniqueRooms.get(key)
    if (!set) { set = new Set<string>(); uniqueRooms.set(key, set) }
    set.add(roomKey)
    allRooms.add(`${orgId}/${roomKey}`)
  }

  const byOrgAndBuilding: RoomDiscoveryByOrgAndBuildingRow[] = []
  for (const [key, totalEvents] of events.entries()) {
    const [orgId, buildingIdRaw] = key.split('::')
    const buildingId = buildingIdRaw === 'unknown' ? null : buildingIdRaw
    byOrgAndBuilding.push({
      organizationId:   orgId,
      organizationName: orgNameById.get(orgId) ?? 'Unknown organization',
      buildingId,
      buildingName:     buildingId ? (buildingNameById.get(buildingId) ?? 'Unknown building') : 'Unknown building',
      totalEvents,
      uniqueRoomCount:  uniqueRooms.get(key)?.size ?? 0,
    })
  }
  byOrgAndBuilding.sort((a, b) =>
    a.organizationName.localeCompare(b.organizationName)
    || b.uniqueRoomCount - a.uniqueRoomCount
    || b.totalEvents - a.totalEvents
    || a.buildingName.localeCompare(b.buildingName),
  )

  return {
    rangeDays,
    totalEvents:     claimRows.length,
    uniqueRoomCount: allRooms.size,
    byOrgAndBuilding,
  }
}
